import './auto-credit-order.scss';

import { Alert, notification } from 'antd';
import { createContext, useEffect, useState } from 'react';

import {
  OrderHistory,
  SystemNotifications
} from '@/components/Shared/entities';
import { AllurLoader, CustomTab } from '@/components/Shared/ui';
import { DictTypes, SELECTED_BANK_STATUSES } from '@/constants';
import { useAppDispatch, useAppSelector } from '@/store';
import { setHeaderConfig } from '@/store/app.slice';
import {
  setClientInfo,
  setCurrent,
  setNewOrderToInitial,
  setShouldUpdateRequest
} from '@/store/newOrder.slice';
import {
  setBankOffers,
  setShouldUpdateResponses
} from '@/store/offerStep.slice';

import {
  HeaderInfo,
  HorizontalInfo,
  OnlineRequestStatusChanger,
  TakeToWorkButton,
  TakenUserInfoButton,
  UpdateStatusContext,
  UpdateStatusModal
} from './entities';
import { FinancingStep } from './financing-step/financing-step';
import useAutoCredRequestId from './hooks/useAutoCredRequestId';
import useGetOrderData from './hooks/useGetOrderData';
import useRefreshOrderData from './hooks/useRefreshOrderData';
import { OfferStep } from './offer-step/offer-step';
import { OrderStep } from './order-step/order-step';

export const CreateTabContext = createContext({ isFieldsFrozen: false });

const checkedTabs: Record<string, number[]> = {
  draft: [],
  financed: [0, 1, 2]
};
const items = [
  {
    key: 'order',
    title: 'Заявка'
  },
  {
    key: 'offer',
    title: 'Предложения'
  },
  {
    key: 'financing',
    title: 'Финансирование'
  }
];

const ExistedOrder = () => {
  const {
    userAccesses: {
      autoCredit: {
        canChangeStatusOnlineRequest,
        canTakeToWorkOnlineRequest,
        canViewOnlineRequestHistory
      }
    }
  } = useAppSelector((state) => state.userInfo);
  const { current, orderApp, shouldUpdateRequest } = useAppSelector(
    (state) => state.newOrder
  );

  const { requestId } = useAutoCredRequestId();

  const { selectedBank, selectedLeasing } = useAppSelector(
    (state) => state.financingStep
  );
  const { shouldUpdateResponses } = useAppSelector((state) => state.offerStep);
  const { dicts } = useAppSelector((state) => state.dict);
  const dispatch = useAppDispatch();

  const isBiometricSessionFailed =
    !!orderApp?.biometricSession &&
    !orderApp.biometricSession.isLoading &&
    (orderApp.biometricSession.isExpired ||
      !orderApp.biometricSession.isValidated);

  const isFieldsFrozen: boolean =
    orderApp?.status !== 'draft' || isBiometricSessionFailed;
  const isFinanced: boolean =
    orderApp?.status === 'financed' ||
    selectedBank?.status.code === 'financed' ||
    selectedLeasing?.status.code === 'financed';
  const orderStatus: string = isFinanced ? 'financed' : orderApp?.status || '';

  const isTakenByUser = !!orderApp?.onlineClientRequest?.takenByUser;

  const [historyModal, setHistoryModal] = useState<boolean>(false);
  const [updateStatusModal, setUpdateStatusModal] = useState('');
  const [isUpdateStatusFinancing, setIsUpdateStatusFinancing] = useState(false);

  const { isLoading } = useGetOrderData();
  const { refreshOrderData } = useRefreshOrderData({
    allowWithOrder: true,
    autoFetch: false
  });

  const handleNextStep = () => {
    dispatch(setCurrent(current + 1));
  };

  const handleStepCurrentChange = (val: number) => {
    if (isBiometricSessionFailed) return;
    if (
      orderApp &&
      orderApp?.status &&
      (orderApp.sentTo === 'banks_leasing'
        ? !selectedBank && !selectedLeasing
        : orderApp.sentTo === 'banks'
          ? !selectedBank
          : !selectedLeasing) &&
      val === 2
    ) {
      if (canTakeToWorkOnlineRequest) {
        notification.warning({
          title: 'Возьмите заявку в работу',
          description:
            'Для перехода на данный этап, вам необходимо взять заявку в работу и выбрать предложение банка',
          duration: 5
        });
      } else {
        notification.warning({
          title:
            orderApp.sentTo === 'banks_leasing'
              ? 'Выберите предложение банка или лизинга'
              : orderApp.sentTo === 'leasing'
                ? 'Выберите предложение лизинга'
                : 'Выберите предложение банка',
          description:
            'Чтобы перейти на данный этап, вам необходимо выбрать предложение банка',
          duration: 5
        });
      }
    } else if (orderApp && orderApp?.status !== 'draft') {
      dispatch(setCurrent(val));
    } else if (orderApp?.status === 'draft' && val === 0) {
      dispatch(setCurrent(val));
    }
  };

  // useEffect(() => {
  //   if (
  //     !isAdmin &&
  //     dealer &&
  //     orderApp &&
  //     dealer.value !== orderApp.productInfo.product.chosenDealer
  //   ) {
  //     notification.warning({
  //       title: 'Выполнено перенаправление на главную страницу',
  //       description: 'Просмотр заявок других дилеров запрещён'
  //     });
  //     navigate(ROUTES.autoCredit);
  //   }
  // }, [isAdmin, dealer, orderApp]);

  useEffect(() => {
    if (!shouldUpdateRequest) return;
    refreshOrderData(orderApp?.uuid || requestId, { skipResponsesFetch: true });
    dispatch(setShouldUpdateRequest(false));
  }, [shouldUpdateRequest, refreshOrderData, orderApp?.uuid, requestId]);

  useEffect(() => {
    if (!shouldUpdateResponses) return;
    refreshOrderData(orderApp?.uuid || requestId, { skipRequestFetch: true });
    dispatch(setShouldUpdateResponses(false));
  }, [shouldUpdateResponses, refreshOrderData, orderApp?.uuid, requestId]);

  useEffect(() => {
    return () => {
      dispatch(setNewOrderToInitial());
      dispatch(setClientInfo(null));
      dispatch(setCurrent(0));
      dispatch(setBankOffers([]));
    };
  }, []);

  useEffect(() => {
    dispatch(
      setHeaderConfig({
        title: orderApp?.isOnlineRequest ? 'Заявка (онлайн)' : 'Заявка',
        helmetTitle: `Автокредитование - ${orderApp?.uuid}`,
        hasBackArrow: true
      })
    );

    return () => {
      dispatch(setHeaderConfig({}));
    };
  }, [orderApp]);

  if (isLoading) {
    return (
      <div key="new-order" className="auto-credit-order">
        <AllurLoader fullScreen />
      </div>
    );
  }

  return (
    <UpdateStatusContext.Provider
      value={{
        closeUpdateStatusModal: () => {
          setUpdateStatusModal('');
          setIsUpdateStatusFinancing(false);
        },
        onUpdateOfferStatus: (id: string, isFinancingStep = false) => {
          setUpdateStatusModal(id);
          setIsUpdateStatusFinancing(isFinancingStep);
        }
      }}
    >
      <div className="auto-credit-order">
        <div className="auto-credit-order__steps-wrapper">
          <CustomTab
            activeTab={items[current].key}
            setActiveTab={(value) =>
              handleStepCurrentChange(
                items.findIndex((item) => item.key === value)
              )
            }
            tabs={items}
            alwaysChecked={
              checkedTabs[orderStatus] ??
              (SELECTED_BANK_STATUSES.includes(orderStatus) ? [0, 1] : [0])
            }
            finishedText={isFinanced ? 'Профинансирован' : undefined}
          />
        </div>

        <SystemNotifications marginFrom="top" />

        <div className="auto-credit-order__container">
          <div className="auto-credit-order__client-info">
            <HeaderInfo setHistoryModal={setHistoryModal} />
          </div>

          <HorizontalInfo setHistoryModal={setHistoryModal} />

          {isBiometricSessionFailed && (
            <Alert
              title={
                orderApp.biometricSession?.isExpired
                  ? 'Истек срок действия биометрической сессии'
                  : dicts?.[DictTypes.Statuses]?.getBy.code[orderApp.status]
                      ?.name || orderApp.status
              }
              type="error"
              showIcon
              className="auto-credit-order__biometric-session-failed-alert"
            />
          )}

          <div className="auto-credit-order__content">
            {current === 0 && (
              <CreateTabContext.Provider value={{ isFieldsFrozen }}>
                <OrderStep createRequest={handleNextStep} />
              </CreateTabContext.Provider>
            )}
            {current === 1 && <OfferStep handleAcceptOffer={handleNextStep} />}
            {current === 2 && <FinancingStep />}
          </div>

          {orderApp?.isOnlineRequest && (
            <div className="auto-credit-order__online-request-status-wrapper">
              {!isTakenByUser ? (
                canTakeToWorkOnlineRequest && <TakeToWorkButton />
              ) : (
                <>
                  {canChangeStatusOnlineRequest && (
                    <OnlineRequestStatusChanger />
                  )}

                  <TakenUserInfoButton />
                </>
              )}
            </div>
          )}
        </div>

        <UpdateStatusModal
          bankId={updateStatusModal}
          isFinancingStep={isUpdateStatusFinancing}
          isOpened={!!updateStatusModal}
          onClose={() => {
            setUpdateStatusModal('');
            setIsUpdateStatusFinancing(false);
          }}
        />

        <OrderHistory
          historyType="auto-credit"
          isOpened={historyModal}
          onClose={setHistoryModal}
          showOnlineAutoCreditHistory={
            !!orderApp?.isOnlineRequest && canViewOnlineRequestHistory
          }
        />
      </div>
    </UpdateStatusContext.Provider>
  );
};

export default ExistedOrder;
